import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  Shield,
  ShieldCheck,
  ShieldX,
  Save,
  RotateCcw,
  Settings,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  AlertCircle,
  Info
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { cn } from '../../utils/cn'

const ClientPermissions = ({ clientId }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [localPermissions, setLocalPermissions] = useState({})
  const [showDetails, setShowDetails] = useState(false)
  const queryClient = useQueryClient()

  // Fetch client permissions
  const { data: permissionData, isLoading, error } = useQuery(
    ['client-permissions', clientId],
    () => apiService.get(`/permissions/client/${clientId}`).then(res => res.data),
    { 
      enabled: !!clientId,
      refetchOnWindowFocus: false 
    }
  )

  // Fetch available permissions for metadata
  const { data: availablePermissions } = useQuery(
    'available-permissions',
    () => apiService.get('/permissions/available').then(res => res.data),
    { 
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  )

  // Update permissions mutation
  const updatePermissionsMutation = useMutation(
    (permissions) => apiService.put(`/permissions/client/${clientId}`, { permissions }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-permissions', clientId])
        setIsEditing(false)
        toast.success('Permissions updated successfully')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update permissions')
      }
    }
  )

  // Reset permissions mutation
  const resetPermissionsMutation = useMutation(
    (preset) => apiService.post(`/permissions/client/${clientId}/reset`, { preset }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['client-permissions', clientId])
        setIsEditing(false)
        toast.success(`Permissions reset to ${data.data.preset} preset`)
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to reset permissions')
      }
    }
  )

  const startEditing = () => {
    setLocalPermissions({ ...permissionData.permissions })
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setLocalPermissions({})
    setIsEditing(false)
  }

  const handlePermissionChange = (permission, value) => {
    setLocalPermissions({
      ...localPermissions,
      [permission]: value
    })
  }

  const savePermissions = () => {
    updatePermissionsMutation.mutate(localPermissions)
  }

  const resetToPreset = (preset) => {
    if (confirm(`Are you sure you want to reset permissions to ${preset} preset? This will overwrite all current settings.`)) {
      resetPermissionsMutation.mutate(preset)
    }
  }

  const getPermissionIcon = (hasPermission) => {
    return hasPermission ? (
      <CheckCircle className="h-4 w-4 text-success-500" />
    ) : (
      <XCircle className="h-4 w-4 text-error-500" />
    )
  }

  const getCategoryColor = (category) => {
    const colors = {
      'Content': 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-200',
      'Site Management': 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-200',
      'Analytics': 'bg-accent-100 text-accent-800 dark:bg-accent-900/20 dark:text-accent-200',
      'Support': 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-200',
      'Billing': 'bg-error-100 text-error-800 dark:bg-error-900/20 dark:text-error-200',
      'Data': 'bg-neutral-100 text-neutral-800 dark:bg-neutral-900/20 dark:text-neutral-200'
    }
    return colors[category] || colors['Data']
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <Shield className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <span>Client Permissions</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" text="Loading permissions..." />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error || !permissionData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <Shield className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <span>Client Permissions</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-error-400 mx-auto mb-4" />
            <p className="text-neutral-600 dark:text-neutral-400">
              Failed to load permissions
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const permissions = isEditing ? localPermissions : permissionData.permissions
  const groupedPermissions = availablePermissions?.permissionsByCategory || {}

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
              <Shield className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <span>Client Permissions</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
              className="ml-2 p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
              title={showDetails ? "Hide details" : "Show details"}
            >
              {showDetails ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </CardTitle>
          
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDetails(!showDetails)}
                >
                  <Settings className="h-4 w-4 mr-1" />
                  {showDetails ? 'Hide' : 'Details'}
                </Button>
                <Button
                  size="sm"
                  onClick={startEditing}
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Edit Permissions
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelEditing}
                  disabled={updatePermissionsMutation.isLoading}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={savePermissions}
                  disabled={updatePermissionsMutation.isLoading}
                >
                  {updatePermissionsMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-1" />
                      Save Changes
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Quick Stats */}
        <div className="flex items-center justify-between mb-6 p-4 bg-neutral-50 dark:bg-[#1f1c2c] rounded-lg border border-neutral-200 dark:border-[#2f2b41]">
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-success-600">
                {Object.values(permissions).filter(Boolean).length}
              </div>
              <div className="text-xs text-neutral-500">Enabled</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-error-600">
                {Object.values(permissions).filter(p => !p).length}
              </div>
              <div className="text-xs text-neutral-500">Disabled</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-neutral-600">
                {Object.keys(permissions).length}
              </div>
              <div className="text-xs text-neutral-500">Total</div>
            </div>
          </div>

          {isEditing && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-neutral-500">Quick presets:</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => resetToPreset('basic')}
                disabled={resetPermissionsMutation.isLoading}
                className="px-2 py-1 text-xs"
              >
                Basic
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => resetToPreset('standard')}
                disabled={resetPermissionsMutation.isLoading}
                className="px-2 py-1 text-xs"
              >
                Standard
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => resetToPreset('full')}
                disabled={resetPermissionsMutation.isLoading}
                className="px-2 py-1 text-xs"
              >
                Full Access
              </Button>
            </div>
          )}
        </div>

        {/* Permissions List */}
        <div className="space-y-6">
          {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
            <div key={category}>
              <div className="flex items-center mb-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(category)}`}>
                  {category}
                </span>
                <span className="ml-2 text-sm text-neutral-500">
                  ({categoryPermissions.filter(p => permissions[p.key]).length}/{categoryPermissions.length} enabled)
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {categoryPermissions.map((permission) => (
                  <PermissionItem
                    key={permission.key}
                    permission={permission}
                    hasPermission={permissions[permission.key]}
                    isEditing={isEditing}
                    showDetails={showDetails}
                    onChange={(value) => handlePermissionChange(permission.key, value)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-700">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-primary-500 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-primary-700 dark:text-primary-300">
              <p className="font-medium mb-1">Permission Guidelines:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong>Basic:</strong> Limited access - suitable for content reviewers</li>
                <li><strong>Standard:</strong> Most features enabled - suitable for typical clients</li>
                <li><strong>Full Access:</strong> All permissions enabled - use with caution</li>
                <li>Changes take effect immediately and apply to all client dashboard features</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Individual Permission Item Component
const PermissionItem = ({ permission, hasPermission, isEditing, showDetails, onChange }) => {
  return (
    <div className={cn(
      'p-3 border rounded-lg transition-colors',
      hasPermission 
        ? 'border-success-200 bg-success-50 dark:border-success-700 dark:bg-success-900/20' 
        : 'border-neutral-200 bg-neutral-50 dark:border-[#2f2b41] dark:bg-[#1f1c2c]'
    )}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            {getPermissionIcon(hasPermission)}
            <span className="font-medium text-sm text-neutral-900 dark:text-neutral-100">
              {permission.name}
            </span>
          </div>
          
          {showDetails && (
            <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
              {permission.description}
            </p>
          )}
        </div>

        {isEditing && (
          <div className="ml-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={hasPermission}
                onChange={(e) => onChange(e.target.checked)}
                className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-800"
              />
            </label>
          </div>
        )}
      </div>
    </div>
  )
}

const getPermissionIcon = (hasPermission) => {
  return hasPermission ? (
    <CheckCircle className="h-4 w-4 text-success-500" />
  ) : (
    <XCircle className="h-4 w-4 text-error-500" />
  )
}

export default ClientPermissions